<div class="dashboard-container simulation-page">
  <main class="dashboard-content">
    <header class="page-header">
      <div class="page-header-top">
        <h2 class="title mb-0">Simula tu liquidación</h2>
        <span class="supply-chip" [style.--chip-color]="afColor">
          <i-lucide name="droplets" [size]="16" aria-hidden="true"></i-lucide>
          Agua fría
        </span>
      </div>
      <p class="page-subtitle">
        Calcula el importe y el consumo desde tu última lectura liquidada hasta la cifra actual del
        contador.
      </p>

      <p-message severity="warn" styleClass="disclaimer-message">
        <div class="disclaimer-content">
          <span> Esta simulación es meramente informativa y no tiene validez oficial. </span>
          <button
            type="button"
            class="disclaimer-toggle"
            (click)="toggleHelp()"
            [attr.aria-expanded]="helpExpanded()"
          >
            {{ helpExpanded() ? 'Menos información' : 'Más información' }}
          </button>
        </div>
        @if (helpExpanded()) {
          <div class="disclaimer-extra">
            <p>
              Introduce la fecha de simulación y la lectura actual. Te ayudará a controlar tu
              consumo o a liquidar el gasto de un inquilino.
            </p>
            <p class="mb-0">
              Los importes pueden variar según el periodo y las tarifas aplicadas. Solo disponible
              para contadores de agua fría.
            </p>
          </div>
        }
      </p-message>
    </header>

    @if (!loaded()) {
      <section class="input-panel card-panel" aria-busy="true">
        <div class="panel-title">
          <p-skeleton shape="circle" size="18px" />
          <p-skeleton width="12rem" height="1.05rem" />
        </div>

        <div class="last-reading-kpi simulation-kpi-skeleton" aria-hidden="true">
          <div class="kpi-label-row">
            <p-skeleton width="11rem" height="0.8rem" />
            <p-skeleton width="5rem" height="0.8rem" />
          </div>
          <div class="kpi-values">
            <div class="kpi-item">
              <p-skeleton width="2.5rem" height="0.8rem" />
              <p-skeleton width="6.5rem" height="1.35rem" />
            </div>
            <div class="kpi-item">
              <p-skeleton width="3rem" height="0.8rem" />
              <p-skeleton width="4.5rem" height="1.35rem" />
            </div>
          </div>
        </div>

        <div class="simulation-form">
          <div class="fields-row" aria-hidden="true">
            @for (field of [1, 2, 3]; track field) {
              <div class="field">
                <p-skeleton width="55%" height="0.9rem" />
                <p-skeleton width="100%" height="2.5rem" borderRadius="6px" />
              </div>
            }
          </div>

          <div class="form-actions" aria-hidden="true">
            <p-skeleton width="12.5rem" height="2.5rem" borderRadius="6px" />
          </div>
        </div>
      </section>
    }

    @if (loaded() && !hasAfMeters()) {
      <section class="empty-state" aria-live="polite">
        <div class="empty-icon" [style.--chip-color]="afColor">
          <i-lucide name="droplets" [size]="36" aria-hidden="true"></i-lucide>
        </div>
        <h3>Sin contadores de agua fría</h3>
        <p>No hay contadores de agua fría disponibles en esta propiedad.</p>
        <p-button label="Volver al inicio" icon="pi pi-home" (onClick)="goHome()" />
      </section>
    }

    @if (loaded() && hasAfMeters()) {
      <section class="input-panel card-panel">
        <div class="panel-title">
          <i-lucide name="calculator" [size]="18" aria-hidden="true"></i-lucide>
          <h3>Datos de la simulación</h3>
        </div>

        <form [formGroup]="form" class="simulation-form">
          @if (showMeterSelect()) {
            <div class="field">
              <label for="meterId">Contador</label>
              <p-select
                inputId="meterId"
                formControlName="meterId"
                [options]="afMeters()"
                optionValue="meterId"
                placeholder="Selecciona un contador"
                [fluid]="true"
              >
                <ng-template #selectedItem>
                  @if (selectedMeter(); as meter) {
                    {{ meterLabel(meter) }}
                  }
                </ng-template>
                <ng-template #item let-meter>
                  {{ meterLabel(meter) }}
                </ng-template>
              </p-select>
            </div>
          }

          @if (selectedMeter(); as meter) {
            <div class="last-reading-kpi" aria-label="Última lectura liquidada">
              <div class="kpi-label-row">
                <span class="kpi-eyebrow">Última lectura liquidada</span>
                @if (!showMeterSelect()) {
                  <span class="kpi-meter">{{ meterLabel(meter) }}</span>
                }
              </div>
              <div class="kpi-values">
                <div class="kpi-item">
                  <span class="label">Fecha</span>
                  <span class="value">
                    @if (meter.lastReadingDate) {
                      {{ meter.lastReadingDate | date: 'dd/MM/yyyy' }}
                    } @else {
                      —
                    }
                  </span>
                </div>
                <div class="kpi-item">
                  <span class="label">Lectura</span>
                  <span class="value highlight">
                    @if (meter.lastReadingValue != null) {
                      {{ meter.lastReadingValue | number: '1.0-2' }}
                      <span class="unit">m³</span>
                    } @else {
                      —
                    }
                  </span>
                </div>
              </div>
              @if (meter.lastReadingValue == null) {
                <p class="kpi-warning">
                  No hay lectura liquidada previa. No es posible simular esta liquidación.
                </p>
              }
            </div>

            <div class="fields-row">
              <div class="field">
                <label for="readingDate">Fecha simulación</label>
                <p-datepicker
                  inputId="readingDate"
                  formControlName="readingDate"
                  dateFormat="dd/mm/yy"
                  [showIcon]="true"
                  [fluid]="true"
                  [minDate]="minSimulationDate()"
                />
                @if (
                  form.controls.readingDate.hasError('dateTooEarly') &&
                  form.controls.readingDate.touched
                ) {
                  <small class="field-error">
                    Debe ser posterior a
                    {{ meter.lastReadingDate | date: 'dd/MM/yyyy' }}
                  </small>
                }
              </div>

              <div class="field" #readingField>
                <label for="reading">Lectura simulación (m³)</label>
                <p-inputNumber
                  inputId="reading"
                  formControlName="reading"
                  [min]="0"
                  placeholder="0"
                  [fluid]="true"
                  [inputStyleClass]="
                    form.controls.reading.invalid && form.controls.reading.dirty
                      ? 'ng-invalid ng-dirty'
                      : ''
                  "
                />
                @if (form.controls.reading.hasError('tooLow') && form.controls.reading.dirty) {
                  <small class="field-error">
                    Debe ser igual o superior a {{ meter.lastReadingValue }}
                  </small>
                }
              </div>

              <div class="field consumption-field">
                <span class="label-static">Consumo estimado (m³)</span>
                <div
                  class="consumption-live"
                  [class.has-value]="estimatedConsumption() != null"
                  aria-live="polite"
                >
                  @if (estimatedConsumption(); as consumption) {
                    <span class="consumption-number">
                      {{ consumption | number: '1.0-2' }}
                      <span class="unit">m³</span>
                    </span>
                  } @else {
                    <span class="consumption-placeholder">—</span>
                  }
                </div>
              </div>
            </div>

            <div class="form-actions">
              <p-button
                label="Simular liquidación"
                icon="pi pi-calculator"
                styleClass="w-100 w-md-auto"
                (onClick)="simulate()"
                [loading]="isSimulating()"
                [disabled]="!canSimulate()"
              />
            </div>
          } @else if (showMeterSelect()) {
            <p class="select-hint">Selecciona un contador para continuar.</p>
          }
        </form>
      </section>
    }

    @if (simulation(); as result) {
      <section class="result-section reveal" aria-live="polite">
        <div class="result-hero card-panel">
          <div class="hero-top">
            <h3 #resultHeading class="hero-title" tabindex="-1">Liquidación simulada</h3>
            <span class="info-chip">Informativa</span>
          </div>

          <div class="hero-total">
            <span class="total-label">Total estimado</span>
            <span class="total-value">{{
              result.totalAmount | currency: 'EUR' : 'symbol' : '1.2-2'
            }}</span>
          </div>

          <div class="hero-metrics">
            <div class="metric">
              <span class="metric-label">Consumo</span>
              <span class="metric-value">
                @if (resultPeriod(); as period) {
                  {{ period.consumption | number: '1.0-2' }}
                  <span class="unit">m³</span>
                } @else {
                  —
                }
              </span>
            </div>
            <div class="metric">
              <span class="metric-label">Periodo</span>
              <span class="metric-value metric-period">
                @if (resultPeriod(); as period) {
                  {{ period.from | date: 'dd/MM/yyyy' }}
                  <span class="period-sep">→</span>
                  {{ period.to | date: 'dd/MM/yyyy' }}
                } @else {
                  —
                }
              </span>
            </div>
          </div>

          <div class="hero-actions">
            <p-button
              label="Descargar liquidación"
              icon="pi pi-download"
              (onClick)="downloadPdf()"
              [loading]="isDownloading()"
            />
            <p-button
              label="Nueva simulación"
              icon="pi pi-refresh"
              severity="secondary"
              [outlined]="true"
              (onClick)="resetSimulation()"
            />
          </div>
        </div>

        <div class="detail-panel card-panel">
          <h4 class="detail-heading">Lecturas</h4>

          <div class="desktop-table">
            <p-table [value]="result.readings">
              <ng-template #header>
                <tr>
                  <th>Suministro</th>
                  <th>Fecha lectura anterior</th>
                  <th>Fecha simulación</th>
                  <th>Lectura anterior (m³)</th>
                  <th>Lectura (m³)</th>
                  <th>Consumo (m³)</th>
                </tr>
              </ng-template>
              <ng-template #body let-row>
                <tr>
                  <td>{{ row.supplyDescription }}</td>
                  <td>{{ row.previousReadingDate | date: 'dd/MM/yyyy' }}</td>
                  <td>{{ row.readingDate | date: 'dd/MM/yyyy' }}</td>
                  <td>{{ row.previousReading | number: '1.0-2' }}</td>
                  <td>{{ row.reading | number: '1.0-2' }}</td>
                  <td class="fw-bold">{{ row.consumption | number: '1.0-2' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <div class="mobile-list">
            @for (row of result.readings; track $index) {
              <div class="summary-block">
                <div class="s-row">
                  <span class="s-label">Suministro</span>
                  <span class="s-value">{{ row.supplyDescription }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Fecha anterior</span>
                  <span class="s-value">{{ row.previousReadingDate | date: 'dd/MM/yyyy' }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Fecha simulación</span>
                  <span class="s-value">{{ row.readingDate | date: 'dd/MM/yyyy' }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Lectura anterior</span>
                  <span class="s-value">{{ row.previousReading | number: '1.0-2' }} m³</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Lectura</span>
                  <span class="s-value">{{ row.reading | number: '1.0-2' }} m³</span>
                </div>
                <div class="s-row total">
                  <span class="s-label">Consumo</span>
                  <span class="s-value">{{ row.consumption | number: '1.0-2' }} m³</span>
                </div>
              </div>
            }
          </div>

          <h4 class="detail-heading mt-4">Conceptos</h4>

          <div class="desktop-table">
            <p-table [value]="result.services">
              <ng-template #header>
                <tr>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Cantidad</th>
                  <th>Importe unitario</th>
                  <th>Importe</th>
                </tr>
              </ng-template>
              <ng-template #body let-row>
                <tr>
                  <td>{{ row.serviceType }}</td>
                  <td>{{ row.description }}</td>
                  <td>{{ row.quantity | number: '1.0-2' }}</td>
                  <td>{{ row.unitAmount | currency: 'EUR' : 'symbol' : '1.2-2' }}</td>
                  <td class="fw-bold">
                    {{ row.totalAmount | currency: 'EUR' : 'symbol' : '1.2-2' }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <div class="mobile-list">
            @for (row of result.services; track row.order) {
              <div class="summary-block">
                <div class="s-row">
                  <span class="s-label">Tipo</span>
                  <span class="s-value">{{ row.serviceType }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Descripción</span>
                  <span class="s-value">{{ row.description }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Cantidad</span>
                  <span class="s-value">{{ row.quantity | number: '1.0-2' }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Importe unitario</span>
                  <span class="s-value">{{
                    row.unitAmount | currency: 'EUR' : 'symbol' : '1.2-2'
                  }}</span>
                </div>
                <div class="s-row total">
                  <span class="s-label">Importe</span>
                  <span class="s-value">{{
                    row.totalAmount | currency: 'EUR' : 'symbol' : '1.2-2'
                  }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      </section>

      <div class="mobile-sticky-bar" aria-label="Acciones de liquidación simulada">
        <div class="sticky-total">
          <span class="label">Total</span>
          <span class="value">{{ result.totalAmount | currency: 'EUR' : 'symbol' : '1.2-2' }}</span>
        </div>
        <p-button
          label="Descargar"
          icon="pi pi-download"
          (onClick)="downloadPdf()"
          [loading]="isDownloading()"
          size="small"
        />
      </div>
    }
  </main>
</div>
